// Improved client/src/pages/admin/UserManagementPage.js with cleaner UI
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Devices as DevicesIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  FolderZip as FolderZipIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  HourglassEmpty as HourglassIcon,
  Info as InfoIcon,
  VpnKey as KeyIcon,
  VerifiedUser as LicenseIcon,
  ManageAccounts as ManageIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Person as UserIcon,
} from "@mui/icons-material";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { licenseDateRange } from "../../../../shared/licenseDates";
import AdminInfoDisplay from "../../components/admin/AdminInfoDisplay";
import UserDownloadLogs from "../../components/admin/UserDownloadLogs";
import UserProductHardwareIds from "../../components/admin/UserProductHardwareIds";
import UserVersionFilesManager from "../../components/admin/UserVersionFilesManager";
import RegisterForm from "../../components/common/RegisterForm";
import LicenseTypeSelector from "../../components/license/LicenseTypeSelector";
import { LicenseType } from "../../constants/licenseTypes";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { getProducts } from "../../services/productService";
import { userProductHardwareService } from "../../services/userProductHardwareService";
import { userProductLicenseService } from "../../services/userProductLicenseService";
import { userService } from "../../services/userService";
import { isLicenseServerAssignment } from "../../utils/licenseServerUtils";
import {
  calculateLicenseStatus,
  getLicenseStatusDisplay,
} from "../../utils/licenseStatusUtils";

import { useNavigate } from "react-router-dom";

// TabPanel component for organizing tabbed content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ padding: "16px 0" }}
    >
      {value === index && children}
    </div>
  );
}

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // { user, product } target for the user-centric zip dialog
  const [zipTarget, setZipTarget] = useState(null);
  const { themeMode } = React.useContext(ThemeContext);
  const [formData, setFormData] = useState({
    email: "",
    role: "user",
    meta: {},
    productIds: [],
    licenseServerIds: [],
    licenseLimits: {},
    licenseTerms: {},
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("email");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloadLogsOpen, setDownloadLogsOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [licenseFormData, setLicenseFormData] = useState({
    hardwareId: "",
    companyName: "",
    expireDate: "",
    licenseType: LicenseType.TRIAL, // Default to SUBSCRIPTION
  });
  const [licenseKey, setLicenseKey] = useState("");
  const [licenseHistoryDialogOpen, setLicenseHistoryDialogOpen] =
    useState(false);
  const [licenseHistory, setLicenseHistory] = useState([]);

  // New state for user detail drawer
  const [userDetailDrawer, setUserDetailDrawer] = useState(false);
  const [currentUserDetail, setCurrentUserDetail] = useState(null);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [userLicenses, setUserLicenses] = useState({});

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  const { user } = useAuth();

  const navigate = useNavigate();
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    licenseStatus: "",
    productId: "",
    expiringDays: "",
  });
  const [filteredData, setFilteredData] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);

  // Get displayed users (filtered or all)
  const displayedUsers = filteredData ? filteredData.users : users;
  const totalCount = filteredData ? filteredData.total : users.length;
  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== "",
  ).length;

  useEffect(() => {
    fetchUsers();
    fetchProducts();
  }, []);

  // Load license status for visible users when users change
  useEffect(() => {
    if (users.length > 0) {
      loadLicenseStatusForUsers(paginatedUsers);
    }
  }, [users, page, rowsPerPage]);

  // Load license status for multiple users (with throttling to avoid overwhelming the server)
  const loadLicenseStatusForUsers = async (userList) => {
    const usersWithLicensedProducts = userList.filter(
      (user) =>
        user.products &&
        user.products.some(
          (p) => p.requires_license && !isLicenseServerAssignment(p),
        ) &&
        !userLicenses[user.id],
    );

    // Process users in batches of 3 to avoid overwhelming the server
    const batchSize = 3;
    for (let i = 0; i < usersWithLicensedProducts.length; i += batchSize) {
      const batch = usersWithLicensedProducts.slice(i, i + batchSize);
      const promises = batch.map((user) => loadUserLicenseStatus(user));

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error("Error loading license status batch:", error);
      }

      // Add a small delay between batches to be gentle on the server
      if (i + batchSize < usersWithLicensedProducts.length) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();

      // Process users to ensure hardware IDs are correctly displayed
      const processedUsers = response;

      setUsers(processedUsers);
      setCurrentUserDetail((previous) =>
        previous
          ? processedUsers.find((item) => item.id === previous.id) || null
          : null,
      );
      setSelectedUser((previous) =>
        previous
          ? processedUsers.find((item) => item.id === previous.id) || null
          : null,
      );
      setError(null);
    } catch (err) {
      setError("Failed to fetch users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  // Filter methods
  const fetchFilteredUsers = async (filterParams) => {
    try {
      setFilterLoading(true);

      // If no filters, reset to show all users
      if (
        Object.keys(filterParams).length === 0 ||
        Object.values(filterParams).every((v) => v === "")
      ) {
        setFilteredData(null);
        setFilters({
          status: "",
          licenseStatus: "",
          productId: "",
          expiringDays: "",
        });
        return;
      }

      const response = await userService.getFilteredUsers(filterParams);

      // Process the filtered users
      const processedUsers = response.users || response;

      setFilteredData({
        users: processedUsers,
        total: response.total || processedUsers.length,
        filters: response.filters || filterParams,
      });

      // Reset pagination when applying filters
      setPage(0);
      // showNotification(`Found \${processedUsers.length} users matching filters\`, "success");
    } catch (err) {
      console.error("Error fetching filtered users:", err);
      showNotification("Failed to apply filters", "error");
      setFilteredData(null);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== "") {
        acc[key] = value;
      }
      return acc;
    }, {});
    fetchFilteredUsers(cleanFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      status: "",
      licenseStatus: "",
      productId: "",
      expiringDays: "",
    });
    setFilteredData(null);
    fetchUsers();
  };

  const handleRefreshAll = () => {
    setFilters({
      status: "",
      licenseStatus: "",
      productId: "",
      expiringDays: "",
    });
    setFilteredData(null);
    fetchUsers();
  };

  const handleOpenUserDetail = async (user) => {
    setCurrentUserDetail(user);
    setUserDetailDrawer(true);
    setCurrentTabIndex(0);

    // Load license status for all user's products
    if (user.products && user.products.length > 0) {
      await loadUserLicenseStatus(user);
    }
  };

  // Load license status for a specific user
  const loadUserLicenseStatus = async (user) => {
    try {
      const licenses = {};

      // For each product that requires a license, get the current license status
      for (const product of user.products || []) {
        if (product.requires_license && !isLicenseServerAssignment(product)) {
          try {
            const response = await userProductLicenseService.getCurrentLicense(
              user.id,
              product.id,
            );

            if (response && response.license) {
              // Use the centralized license status calculation
              const status = calculateLicenseStatus(response.license);

              licenses[product.id] = {
                ...response.license,
                status: status,
              };
            } else {
              licenses[product.id] = {
                status: "not_found",
              };
            }
          } catch (err) {
            console.error(
              `Error fetching license for product ${product.id}:`,
              err,
            );
            licenses[product.id] = {
              status: "not_found",
            };
          }
        }
      }

      setUserLicenses((prev) => ({
        ...prev,
        [user.id]: licenses,
      }));
    } catch (error) {
      console.error("Error loading user license status:", error);
    }
  };

  // Get license status chip component
  const getLicenseStatusChip = (product, userId) => {
    const license = userLicenses[userId]?.[product.id];

    if (!product.requires_license || isLicenseServerAssignment(product)) {
      return null;
    }

    if (!license) {
      return (
        <Chip
          size="small"
          label="Loading..."
          color="default"
          sx={{ height: 20, fontSize: "0.6rem" }}
        />
      );
    }

    // Use centralized license status display configuration
    const statusDisplay = getLicenseStatusDisplay(license.status);

    // Map our license status to colors for the chip
    const getChipColor = (status) => {
      switch (status) {
        case "active":
          return "success";
        case "expired":
          return "error";
        case "expiring_soon":
          return "warning";
        case "inactive":
          return "warning";
        case "pending":
          return "info";
        case "not_found":
        default:
          return "default";
      }
    };

    return (
      <Chip
        size="small"
        label={statusDisplay.label}
        color={getChipColor(license.status)}
        icon={<LicenseIcon fontSize="small" />}
        sx={{ height: 20, fontSize: "0.6rem" }}
      />
    );
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      role: user.role,
      meta: user.meta || {},
      metaString: JSON.stringify(user.meta || {}, null, 2),
      licenseTerms: Object.fromEntries(
        (user.products || []).map((p) => [
          p.id,
          {
            licenseType:
              p.UserProduct?.license_type ?? p.user_products?.license_type ?? 1,
            licenseMonths:
              p.UserProduct?.license_months ??
              p.user_products?.license_months ??
              1,
          },
        ]),
      ),
      licenseLimits: Object.fromEntries(
        (user.products || []).map((p) => [
          p.id,
          p.UserProduct?.license_limit ?? p.user_products?.license_limit ?? 1,
        ]),
      ),
      productIds: user.products ? user.products.map((p) => p.id) : [],
      licenseServerIds: user.products
        ? user.products
            .filter(
              (p) =>
                p.UserProduct?.is_license_server ??
                p.user_products?.is_license_server,
            )
            .map((p) => p.id)
        : [],
    });
    setEditDialogOpen(true);
  };

  const handleCreateUser = () => {
    setCreateUserDialogOpen(true);
  };

  const handleUserCreated = (newUser) => {
    setCreateUserDialogOpen(false);
    fetchUsers(); // Refresh the user list
  };

  const handleDeleteClick = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await userService.deleteUser(userId);
        fetchUsers();
        // Close user details drawer if the deleted user is currently viewed
        if (currentUserDetail && currentUserDetail.id === userId) {
          setUserDetailDrawer(false);
        }
      } catch (err) {
        setError("Failed to delete user");
        console.error("Error deleting user:", err);
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMetaChange = (e) => {
    // Store the raw input
    setFormData((prev) => ({
      ...prev,
      metaString: e.target.value,
    }));
  };

  const handleProductToggle = (productId) => {
    setFormData((prev) => {
      const productIds = [...prev.productIds];
      const licenseServerIds = [...prev.licenseServerIds];
      const index = productIds.indexOf(productId);

      if (index > -1) {
        productIds.splice(index, 1);
        const lsIndex = licenseServerIds.indexOf(productId);
        if (lsIndex > -1) licenseServerIds.splice(lsIndex, 1);
      } else {
        productIds.push(productId);
      }

      return {
        ...prev,
        productIds,
        licenseServerIds,
      };
    });
  };

  const handleLicenseServerToggle = (productId) => {
    setFormData((prev) => {
      const licenseServerIds = [...prev.licenseServerIds];
      const index = licenseServerIds.indexOf(productId);

      if (index > -1) {
        licenseServerIds.splice(index, 1);
      } else {
        licenseServerIds.push(productId);
      }

      return { ...prev, licenseServerIds };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let dataToSubmit = { ...formData };

      // Parse the metaString back to an object when submitting
      if (formData.metaString) {
        try {
          dataToSubmit.meta = JSON.parse(formData.metaString);
          delete dataToSubmit.metaString; // Remove the string version
        } catch (err) {
          alert("Invalid JSON in meta data. Please correct and try again.");
          return; // Stop submission if JSON is invalid
        }
      }

      // Replace productIds with the richer products shape so the backend
      // receives the license-server flag alongside each assignment.
      dataToSubmit.products = formData.productIds.map((id) => ({
        productId: id,
        isLicenseServer: formData.licenseServerIds.includes(id),
        licenseLimit: Number(formData.licenseLimits?.[id] ?? 1),
        licenseType: Number(formData.licenseTerms?.[id]?.licenseType ?? 1),
        licenseMonths: Number(formData.licenseTerms?.[id]?.licenseMonths ?? 1),
      }));
      delete dataToSubmit.productIds;
      delete dataToSubmit.licenseServerIds;
      delete dataToSubmit.licenseLimits;
      delete dataToSubmit.licenseTerms;

      if (selectedUser) {
        await userService.updateUser(selectedUser.id, dataToSubmit);
      }
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError("Failed to update user");
      console.error("Error updating user:", err);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDownloadLogs = (userId) => {
    setSelectedUserId(userId);
    setDownloadLogsOpen(true);
  };

  // License management functions
  const handleGenerateLicense = async (
    userId,
    product,
    prefilledHardwareId = "",
  ) => {
    // If called with event, it's a form submission
    if (userId && userId.preventDefault) {
      const e = userId;
      e.preventDefault();

      // Check if hardware ID is confirmed before generating license
      try {
        const hardwareSubmissions =
          await userProductHardwareService.getUserHardwareSubmissions(
            selectedUserId,
          );
        const confirmedSubmission = hardwareSubmissions.submissions?.find(
          (sub) =>
            sub.product.id === selectedProduct.id &&
            sub.hardware_id === licenseFormData.hardwareId &&
            sub.is_confirmed &&
            sub.is_locked,
        );

        if (!confirmedSubmission) {
          alert(
            "Hardware ID must be submitted by user and confirmed by admin before license generation.",
          );
          return;
        }

        const response = await userProductLicenseService.generateLicense(
          selectedUserId,
          selectedProduct.id,
          licenseFormData,
        );

        if (response && response.license) {
          alert(
            `License generated successfully! License key: ${response.license.license_key}`,
          );
        } else {
          alert("License generated but no key was returned.");
        }
        setLicenseDialogOpen(false);
        setUserLicenses({});
        await fetchUsers();
      } catch (err) {
        alert(
          "Failed to generate license: " +
            (err.response?.data?.message || err.message || "Unknown error"),
        );
        console.error("Error generating license:", err);
      }
      return;
    }

    // Otherwise initialize the license dialog
    setSelectedUserId(userId);
    setSelectedProduct(product);

    // Pre-fill with confirmed hardware ID if available
    let hardwareIdToUse = prefilledHardwareId;
    if (!hardwareIdToUse) {
      // Try to get confirmed hardware ID for this user-product combination
      try {
        const hardwareSubmissions =
          await userProductHardwareService.getUserHardwareSubmissions(userId);
        const confirmedSubmission = hardwareSubmissions.submissions?.find(
          (sub) =>
            sub.product.id === product.id && sub.is_confirmed && sub.is_locked,
        );
        if (confirmedSubmission) {
          hardwareIdToUse = confirmedSubmission.hardware_id;
        }
      } catch (err) {
        console.error("Error fetching hardware submissions:", err);
      }
    }

    setLicenseFormData({
      hardwareId: hardwareIdToUse || "",
      companyName: "",
      expireDate: licenseDateRange(
        product.UserProduct?.license_months ??
          product.user_products?.license_months ??
          1,
      ).max,
      licenseType: LicenseType.TRIAL, // Default to SUBSCRIPTION
    });
    setLicenseDialogOpen(true);
  };

  const handleLicenseFormChange = (e) => {
    const { name, value } = e.target;
    setLicenseFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLicenseTypeChange = (licenseType) => {
    setLicenseFormData((prev) => ({
      ...prev,
      licenseType: licenseType,
    }));
  };

  const handleViewLicenseHistory = async (userId, product) => {
    try {
      setSelectedUserId(userId);
      setSelectedProduct(product);
      const response = await userProductLicenseService.getLicenseHistory(
        userId,
        product.id,
      );
      setLicenseHistory(response.licenses || []);
      setLicenseHistoryDialogOpen(true);
    } catch (err) {
      alert(
        "Failed to retrieve license history: " +
          (err.message || "Unknown error"),
      );
      console.error("Error retrieving license history:", err);
    }
  };

  // Password change handlers
  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setPasswordFormData({
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({});
    setPasswordDialogOpen(true);
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordFormData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (passwordFormData.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    }

    if (!passwordFormData.confirmPassword) {
      errors.confirmPassword = "Please confirm the password";
    } else if (
      passwordFormData.newPassword !== passwordFormData.confirmPassword
    ) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      await userService.changeUserPassword(selectedUser.id, {
        newPassword: passwordFormData.newPassword,
      });

      alert("Password changed successfully!");
      setPasswordDialogOpen(false);
      setPasswordFormData({ newPassword: "", confirmPassword: "" });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to change password";
      alert("Error: " + errorMessage);
      console.error("Error changing password:", err);
    }
  };

  const filteredUsers = displayedUsers
    .filter(
      (user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const isAsc = order === "asc";
      if (orderBy === "email") {
        return isAsc
          ? a.email.localeCompare(b.email)
          : b.email.localeCompare(a.email);
      }
      if (orderBy === "role") {
        return isAsc
          ? a.role.localeCompare(b.role)
          : b.role.localeCompare(a.role);
      }
      return 0;
    });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Calculate number of products for each user
  const getUserProductsCount = (user) => {
    return user.products ? user.products.length : 0;
  };

  // Calculate number of hardware IDs for each user
  const getUserHardwareCount = (user) => {
    const productSpecificCount =
      (user.product_hardware_submissions &&
        user.product_hardware_submissions.length) ||
      0;
    return productSpecificCount;
  };

  // Check if user has license issues (expired, expiring soon, or inactive licenses)
  const hasLicenseIssues = (user) => {
    if (!user.products) return false;

    const licensedProducts = user.products.filter(
      (p) => p.requires_license && !isLicenseServerAssignment(p),
    );
    if (licensedProducts.length === 0) return false;

    // Check each licensed product for issues
    for (const product of licensedProducts) {
      const license = userLicenses[user.id]?.[product.id];

      // Skip if no license assigned yet (this is not an "issue", just not assigned)
      if (!license || !license.license_key) {
        continue;
      }

      // Check license status for actual issues
      if (
        license.status === "expired" ||
        license.status === "expiring_soon" ||
        license.status === "inactive"
      ) {
        return true;
      }
    }

    return false;
  };

  // Check if user has any valid licenses
  const hasValidLicenses = (user) => {
    if (!user.products) return false;

    const licensedProducts = user.products.filter(
      (p) => p.requires_license && !isLicenseServerAssignment(p),
    );
    if (licensedProducts.length === 0) return false;

    // Check if at least one product has a valid license
    for (const product of licensedProducts) {
      const license = userLicenses[user.id]?.[product.id];

      // Has a license and it's active
      if (
        license &&
        license.license_key &&
        (license.status === "active" || !license.status)
      ) {
        return true;
      }
    }

    return false;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>

      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: "transparent",
          border: "none",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <CardHeader
          sx={{
            mb: 0,
          }}
          title={
            <Typography variant="h5" fontWeight="bold">
              User Management
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" color="text.secondary">
              Manage user accounts, permissions, and product access
            </Typography>
          }
          avatar={
            <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
              <ManageIcon fontSize="large" />
            </Avatar>
          }
          action={
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateUser}
                color="primary"
              >
                Create User
              </Button>
              <Button
                variant="outlined"
                onClick={handleRefreshAll}
                startIcon={<RefreshIcon />}
                size="medium"
              >
                Refresh All
              </Button>
            </Box>
          }
        />

        <CardContent sx={{ px: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Statistics Cards */}
          <Grid container spacing={1.5} sx={{ mb: 8 }}>
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 1,
                  textAlign: "center",
                  background:
                    themeMode === "dark"
                      ? "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <GroupIcon sx={{ fontSize: 20, mb: 0.25 }} />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "1.1rem" }}
                >
                  {filteredData?.total || users.length}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                  Total Users
                  {filteredData?.total &&
                    filteredData.total !== users.length && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontSize: "0.6rem" }}
                      >
                        (Filtered: {users.length})
                      </Typography>
                    )}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 1,
                  textAlign: "center",
                  background:
                    themeMode === "dark"
                      ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                      : "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)",
                  color: "white",
                }}
              >
                <SecurityIcon sx={{ fontSize: 20, mb: 0.25 }} />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "1.1rem" }}
                >
                  {displayedUsers.filter((u) => u.is_active).length}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                  Active Users
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                sx={{
                  p: 1,
                  textAlign: "center",
                  background:
                    themeMode === "dark"
                      ? "linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)"
                      : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "white",
                }}
              >
                <AdminIcon sx={{ fontSize: 20, mb: 0.25 }} />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "1.1rem" }}
                >
                  {displayedUsers.filter((u) => u.role === "admin").length}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                  Admin Users
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Combined User Management Section */}

          <Box>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
            >
              <FilterIcon sx={{ fontSize: 16, mr: 0.5 }} /> Filter Options
            </Typography>
            <Grid container spacing={2}>
              {/* Search Filter */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by email, role, or hardware ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* User Status Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>User Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    label="User Status"
                  >
                    <MenuItem value="">
                      <em>All Users</em>
                    </MenuItem>
                    <MenuItem value="active">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="Active" color="success" size="small" />
                        <span>Active Users</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="inactive">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="Inactive" color="default" size="small" />
                        <span>Inactive Users</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* License Status Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>License Status</InputLabel>
                  <Select
                    value={filters.licenseStatus}
                    onChange={(e) =>
                      handleFilterChange("licenseStatus", e.target.value)
                    }
                    label="License Status"
                  >
                    <MenuItem value="">
                      <em>All Licenses</em>
                    </MenuItem>
                    <MenuItem value="active">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="Active" color="primary" size="small" />
                        <span>Active License</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="expired">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="Expired" color="error" size="small" />
                        <span>Expired License</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="none">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="None" variant="outlined" size="small" />
                        <span>No License</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Product Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={filters.productId}
                    onChange={(e) =>
                      handleFilterChange("productId", e.target.value)
                    }
                    label="Product"
                  >
                    <MenuItem value="">
                      <em>All Products</em>
                    </MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Expiring Days Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Licenses Expiring In (days)"
                  value={filters.expiringDays}
                  sx={{ bgcolor: "transparent" }}
                  onChange={(e) =>
                    handleFilterChange("expiringDays", e.target.value)
                  }
                  InputProps={{
                    inputProps: { min: 0, max: 365 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <HourglassIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="caption">Days</Typography>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Enter number of days"
                />
              </Grid>

              {/* Apply Button */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyFilters}
                    startIcon={
                      filterLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SearchIcon sx={{ fontSize: 18 }} />
                      )
                    }
                    disabled={filterLoading || activeFilterCount === 0}
                    sx={{ p: 1 }}
                  >
                    {filterLoading ? "Applying..." : "Apply Filters"}
                  </Button>

                  {activeFilterCount > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleClearFilters}
                      startIcon={<ClearIcon sx={{ fontSize: 18 }} />}
                      sx={{ px: 2, py: 0.75, borderRadius: 1.5 }}
                    >
                      Clear ({activeFilterCount})
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              {/* Active Filters Display */}
              {(activeFilterCount > 0 || searchTerm) && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    <Typography
                      variant="caption"
                      fontWeight="medium"
                      sx={{ mr: 1 }}
                    >
                      Active Filters:
                    </Typography>
                    {searchTerm && (
                      <Chip
                        label={`Search: ${searchTerm}`}
                        onDelete={() => setSearchTerm("")}
                        color="secondary"
                        size="small"
                      />
                    )}
                    {filters.status && (
                      <Chip
                        label={`Status: ${filters.status}`}
                        onDelete={() => handleFilterChange("status", "")}
                        color={
                          filters.status === "active" ? "success" : "default"
                        }
                        size="small"
                      />
                    )}
                    {filters.licenseStatus && (
                      <Chip
                        label={`License: ${filters.licenseStatus}`}
                        onDelete={() => handleFilterChange("licenseStatus", "")}
                        color={
                          filters.licenseStatus === "expired"
                            ? "error"
                            : "primary"
                        }
                        size="small"
                      />
                    )}
                    {filters.productId && (
                      <Chip
                        label={`Product: ${products.find((p) => p.id === parseInt(filters.productId))?.name || filters.productId}`}
                        onDelete={() => handleFilterChange("productId", "")}
                        size="small"
                      />
                    )}
                    {filters.expiringDays && (
                      <Chip
                        label={`Expiring in ${filters.expiringDays} days`}
                        onDelete={() => handleFilterChange("expiringDays", "")}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Chip
                label={`${filteredUsers.length} users found`}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: "medium" }}
              />
            </Box>
          </Box>
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                maxHeight: 600,
                transition: "all 0.2s ease-in-out",
                overflow: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: "bold", width: "252px" }}>
                      <TableSortLabel
                        active={orderBy === "email"}
                        direction={orderBy === "email" ? order : "asc"}
                        onClick={() => handleSort("email")}
                        sx={{
                          color: "text.primary",
                          "&.Mui-active": {
                            color: "primary.main",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "primary.main",
                          },
                        }}
                      >
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "108px" }}>
                      <TableSortLabel
                        active={orderBy === "role"}
                        direction={orderBy === "role" ? order : "asc"}
                        onClick={() => handleSort("role")}
                        sx={{
                          color: "text.primary",
                          "&.Mui-active": {
                            color: "primary.main",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "primary.main",
                          },
                        }}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "108px" }}>
                      Products
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "108px" }}>
                      Licenses
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "108px" }}>
                      Hardware
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "bold", width: "216px" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user, index) => (
                    <TableRow
                      key={user.id}
                      hover
                      onClick={() => handleOpenUserDetail(user)}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                        borderBottom: 1,
                        borderColor: "divider",
                        "& td": {
                          py: 2,
                        },
                      }}
                    >
                      <TableCell sx={{ width: "252px" }}>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1, maxWidth: 350 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                gutterBottom
                              >
                                User Information
                              </Typography>
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    User ID:
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Typography variant="caption">
                                    {user.id}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Email:
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Typography variant="caption">
                                    {user.email}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Role:
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Chip
                                    label={user.role.toUpperCase()}
                                    color={
                                      user.role === "admin"
                                        ? "primary"
                                        : "secondary"
                                    }
                                    size="small"
                                    sx={{ height: 20, fontSize: "0.65rem" }}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          }
                          TransitionComponent={Zoom}
                          placement="top-start"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 0,
                            }}
                          >
                            <Avatar
                              sx={{
                                mr: 2,
                                bgcolor:
                                  user.role === "admin"
                                    ? "primary.main"
                                    : "secondary.main",
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                              }}
                            >
                              {user.role === "admin" ? (
                                <AdminIcon />
                              ) : (
                                <UserIcon />
                              )}
                            </Avatar>
                            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                              <Typography fontWeight="medium" noWrap>
                                {user.email}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                ID: {user.id}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: "108px" }}>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                gutterBottom
                              >
                                User Role
                              </Typography>
                              <Typography variant="body2">
                                {user.role === "admin"
                                  ? "Administrator with full system access"
                                  : "Standard user with limited permissions"}
                              </Typography>
                            </Box>
                          }
                          TransitionComponent={Zoom}
                          placement="top"
                        >
                          <Chip
                            label={user.role.toUpperCase()}
                            color={
                              user.role === "admin" ? "primary" : "secondary"
                            }
                            size="small"
                            sx={{
                              fontWeight: "medium",
                              minWidth: "fit-content",
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: "108px" }}>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1, maxWidth: 300 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                gutterBottom
                              >
                                Assigned Products
                              </Typography>
                              {user.products && user.products.length > 0 ? (
                                <List dense disablePadding sx={{ mt: 1 }}>
                                  {user.products.map((product) => (
                                    <ListItem
                                      key={product.id}
                                      disablePadding
                                      sx={{ py: 0.5 }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              fontWeight="medium"
                                            >
                                              {product.name}
                                            </Typography>
                                            {product.requires_license &&
                                              !isLicenseServerAssignment(
                                                product,
                                              ) && (
                                                <Chip
                                                  label="Requires License"
                                                  color="info"
                                                  size="small"
                                                  sx={{
                                                    height: 18,
                                                    fontSize: "0.6rem",
                                                  }}
                                                />
                                              )}
                                            {isLicenseServerAssignment(
                                              product,
                                            ) && (
                                              <Chip
                                                label="License Server"
                                                color="info"
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                  height: 18,
                                                  fontSize: "0.6rem",
                                                }}
                                              />
                                            )}
                                          </Box>
                                        }
                                        secondary={
                                          product.description && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {product.description.length > 60
                                                ? `${product.description.substring(
                                                    0,
                                                    60,
                                                  )}...`
                                                : product.description}
                                            </Typography>
                                          )
                                        }
                                        primaryTypographyProps={{
                                          variant: "body2",
                                        }}
                                        secondaryTypographyProps={{
                                          variant: "caption",
                                        }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No products assigned to this user
                                </Typography>
                              )}
                            </Box>
                          }
                          TransitionComponent={Zoom}
                          placement="top"
                        >
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Chip
                              label={getUserProductsCount(user)}
                              color={
                                getUserProductsCount(user) > 0
                                  ? "success"
                                  : "default"
                              }
                              variant="outlined"
                              size="small"
                              icon={<AssignmentIcon fontSize="small" />}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* New License Status Column */}
                      <TableCell sx={{ width: "108px" }}>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1, maxWidth: 350 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                gutterBottom
                              >
                                License Status
                              </Typography>
                              {user.products &&
                              user.products.filter(
                                (p) =>
                                  p.requires_license &&
                                  !isLicenseServerAssignment(p),
                              ).length > 0 ? (
                                <List dense disablePadding sx={{ mt: 1 }}>
                                  {user.products
                                    .filter(
                                      (p) =>
                                        p.requires_license &&
                                        !isLicenseServerAssignment(p),
                                    )
                                    .map((product) => {
                                      const license =
                                        userLicenses[user.id]?.[product.id];
                                      return (
                                        <ListItem
                                          key={product.id}
                                          disablePadding
                                          sx={{ py: 0.5 }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  fontWeight="medium"
                                                >
                                                  {product.name}
                                                </Typography>
                                                {getLicenseStatusChip(
                                                  product,
                                                  user.id,
                                                )}
                                              </Box>
                                            }
                                            secondary={
                                              license &&
                                              license.expiry_date && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Expires:{" "}
                                                  {new Date(
                                                    license.expiry_date,
                                                  ).toLocaleDateString()}
                                                </Typography>
                                              )
                                            }
                                            primaryTypographyProps={{
                                              variant: "body2",
                                            }}
                                            secondaryTypographyProps={{
                                              variant: "caption",
                                            }}
                                          />
                                        </ListItem>
                                      );
                                    })}
                                </List>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No licensed products for this user
                                </Typography>
                              )}
                            </Box>
                          }
                          TransitionComponent={Zoom}
                          placement="top"
                        >
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Chip
                              label={
                                user.products
                                  ? user.products.filter(
                                      (p) =>
                                        p.requires_license &&
                                        !isLicenseServerAssignment(p),
                                    ).length
                                  : 0
                              }
                              color={
                                hasLicenseIssues(user)
                                  ? "warning"
                                  : hasValidLicenses(user)
                                    ? "success"
                                    : "default"
                              }
                              variant="outlined"
                              size="small"
                              icon={<LicenseIcon fontSize="small" />}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: "108px" }}>
                        <Tooltip
                          elevation={3}
                          title={
                            <Box
                              sx={{
                                p: 1,
                                maxWidth: 450,
                                maxHeight: 200,
                                overflow: "auto",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                gutterBottom
                              >
                                Hardware IDs
                              </Typography>

                              {/* Product-Specific Hardware IDs */}
                              {user.product_hardware_submissions &&
                                user.product_hardware_submissions.length >
                                  0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      fontWeight="bold"
                                      gutterBottom
                                    >
                                      Product-Specific Hardware IDs:
                                    </Typography>
                                    <Box
                                      sx={{
                                        mt: 1,
                                      }}
                                    >
                                      {user.product_hardware_submissions.map(
                                        (submission, idx) => (
                                          <Box
                                            key={idx}
                                            sx={{
                                              mb: 2,
                                              p: 1,
                                              border: "1px solid",
                                              borderColor: "border.secondary",
                                              borderRadius: 1,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 0.5,
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                fontWeight="bold"
                                                color="text.primary"
                                              >
                                                {submission.product?.name ||
                                                  "Unknown Product"}
                                              </Typography>
                                              {submission.is_confirmed && (
                                                <Chip
                                                  label="Confirmed"
                                                  color="success"
                                                  size="small"
                                                  sx={{
                                                    height: 16,
                                                    fontSize: "0.6rem",
                                                  }}
                                                />
                                              )}
                                              {submission.is_locked && (
                                                <Chip
                                                  label="Locked"
                                                  color="warning"
                                                  size="small"
                                                  sx={{
                                                    height: 16,
                                                    fontSize: "0.6rem",
                                                  }}
                                                />
                                              )}
                                            </Box>
                                            <Typography
                                              variant="caption"
                                              component="div"
                                              sx={{
                                                wordBreak: "break-all",
                                                fontFamily: "monospace",
                                                fontSize: "0.65rem",
                                                bgcolor: "action.hover",
                                                p: 0.5,
                                                borderRadius: 1,
                                              }}
                                            >
                                              {submission.hardware_id}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ fontSize: "0.6rem" }}
                                            >
                                              Submitted:{" "}
                                              {new Date(
                                                submission.submitted_at,
                                              ).toLocaleDateString()}
                                            </Typography>
                                            {submission.confirmedBy && (
                                              <Box sx={{ mt: 0.5 }}>
                                                <AdminInfoDisplay
                                                  admin={submission.confirmedBy}
                                                  label="Confirmed by"
                                                  size="small"
                                                />
                                              </Box>
                                            )}
                                            {submission.approvedBy && (
                                              <Box sx={{ mt: 0.5 }}>
                                                <AdminInfoDisplay
                                                  admin={submission.approvedBy}
                                                  label="Approved by"
                                                  size="small"
                                                />
                                              </Box>
                                            )}
                                          </Box>
                                        ),
                                      )}
                                    </Box>
                                  </Box>
                                )}

                              {(!user.product_hardware_submissions ||
                                user.product_hardware_submissions.length ===
                                  0) && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No hardware IDs registered
                                </Typography>
                              )}
                            </Box>
                          }
                          TransitionComponent={Zoom}
                          placement="top"
                        >
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Chip
                              label={getUserHardwareCount(user)}
                              color={
                                getUserHardwareCount(user) > 0
                                  ? "info"
                                  : "default"
                              }
                              variant="outlined"
                              size="small"
                              icon={<DevicesIcon fontSize="small" />}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: "216px" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            flexWrap: "nowrap",
                          }}
                        >
                          <Tooltip title="Edit User">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(user);
                              }}
                              color="primary"
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(user.id);
                              }}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </CardContent>
      </Card>
      {/* User Detail Drawer */}
      <Drawer
        anchor="right"
        open={userDetailDrawer}
        onClose={() => setUserDetailDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "70%", md: "50%" },
            maxWidth: 700,
            padding: 0,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {currentUserDetail && (
          <>
            <AppBar
              position="static"
              elevation={0}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: "none",
              }}
            >
              <Toolbar sx={{ px: 2, py: 1, justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar
                    sx={{
                      bgcolor:
                        currentUserDetail.role === "admin"
                          ? "primary.main"
                          : "secondary.main",
                      mr: 2,
                    }}
                  >
                    {currentUserDetail.role === "admin" ? (
                      <AdminIcon />
                    ) : (
                      <UserIcon />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {currentUserDetail.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {currentUserDetail.role.toUpperCase()} • User ID:{" "}
                      {currentUserDetail.id}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setUserDetailDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </Toolbar>

              <Tabs
                value={currentTabIndex}
                onChange={(e, newValue) => setCurrentTabIndex(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  "& .MuiTab-root": {
                    minWidth: "auto",
                    py: 1.5,
                    px: 2,
                    color: "text.secondary",
                    "&.Mui-selected": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "primary.main",
                    height: 3,
                  },
                }}
              >
                <Tab
                  label="Overview"
                  icon={<InfoIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Products"
                  icon={<AssignmentIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Hardware & Licenses"
                  icon={<DevicesIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Download History"
                  icon={<HistoryIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </AppBar>

            <Box
              sx={{
                p: 3,
                height: "calc(100% - 128px)",
                overflow: "auto",
                bgcolor: "background.paper",
                color: "text.primary",
              }}
            >
              {/* Overview Tab */}
              <TabPanel value={currentTabIndex} index={0}>
                <Card
                  elevation={0}
                  sx={{
                    mb: 3,
                    border: "none",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <CardHeader title="User Information" />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {currentUserDetail.email}
                        </Typography>
                      </Grid>
                      <Divider orientation="vertical" flexItem />
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Role
                        </Typography>
                        <Typography variant="body1">
                          <Chip
                            label={currentUserDetail.role.toUpperCase()}
                            color={
                              currentUserDetail.role === "admin"
                                ? "primary"
                                : "secondary"
                            }
                            size="small"
                          />
                        </Typography>
                      </Grid>
                      {currentUserDetail.creator && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Created By
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor:
                                    currentUserDetail.creator.role === "admin"
                                      ? "primary.main"
                                      : "secondary.main",
                                }}
                              >
                                {currentUserDetail.creator.role === "admin" ? (
                                  <AdminIcon fontSize="small" />
                                ) : (
                                  <UserIcon fontSize="small" />
                                )}
                              </Avatar>
                              <Typography variant="body1">
                                {currentUserDetail.creator.email}
                              </Typography>
                              <Chip
                                label={currentUserDetail.creator.role.toUpperCase()}
                                color={
                                  currentUserDetail.creator.role === "admin"
                                    ? "primary"
                                    : "secondary"
                                }
                                size="small"
                                sx={{ height: 20 }}
                              />
                            </Box>
                          </Grid>
                        </>
                      )}
                      {currentUserDetail.updater && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Last Updated By
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor:
                                    currentUserDetail.updater.role === "admin"
                                      ? "primary.main"
                                      : "secondary.main",
                                }}
                              >
                                {currentUserDetail.updater.role === "admin" ? (
                                  <AdminIcon fontSize="small" />
                                ) : (
                                  <UserIcon fontSize="small" />
                                )}
                              </Avatar>
                              <Typography variant="body1">
                                {currentUserDetail.updater.email}
                              </Typography>
                              <Chip
                                label={currentUserDetail.updater.role.toUpperCase()}
                                color={
                                  currentUserDetail.updater.role === "admin"
                                    ? "primary"
                                    : "secondary"
                                }
                                size="small"
                                sx={{ height: 20 }}
                              />
                            </Box>
                          </Grid>
                        </>
                      )}
                      <Divider orientation="vertical" flexItem />
                    </Grid>
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    border: "none",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <CardHeader title="Meta Data" />
                  <CardContent>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "action.hover",
                        color: "text.primary",
                        p: 2,
                        borderRadius: 2,
                        overflow: "auto",
                        m: 0,
                        fontFamily:
                          "Monaco, 'Cascadia Code', 'Roboto Mono', monospace",
                        fontSize: "0.875rem",
                        lineHeight: 1.4,
                      }}
                    >
                      {JSON.stringify(currentUserDetail.meta || {}, null, 2)}
                    </Box>
                  </CardContent>
                </Card>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<KeyIcon />}
                    onClick={() => handleChangePassword(currentUserDetail)}
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick(currentUserDetail)}
                  >
                    Edit User
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this user?",
                        )
                      ) {
                        handleDeleteClick(currentUserDetail.id);
                      }
                    }}
                  >
                    Delete User
                  </Button>
                </Box>
              </TabPanel>

              {/* Products Tab */}
              <TabPanel value={currentTabIndex} index={1}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Product Assignment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage which products this user has access to and generate
                    licenses as needed.
                  </Typography>
                </Box>

                {currentUserDetail.products &&
                currentUserDetail.products.length > 0 ? (
                  <List
                    sx={{
                      bgcolor: "action.hover",
                      borderRadius: 2,
                      mb: 3,
                    }}
                  >
                    {currentUserDetail.products.map((product, idx) => {
                      const license =
                        userLicenses[currentUserDetail.id]?.[product.id];
                      const hasActiveLicense =
                        license && license.status === "active";
                      const isExpired = license && license.status === "expired";
                      const isExpiringSoon =
                        license && license.status === "expiring_soon";
                      const isInactive =
                        license && license.status === "inactive";
                      const isMissing = license && license.status === "missing";

                      return (
                        <ListItem
                          key={product.id}
                          divider={idx < currentUserDetail.products.length - 1}
                          secondaryAction={
                            (product.requires_license &&
                              !isLicenseServerAssignment(product) && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      hasActiveLicense
                                        ? "User has active license - Generate new license"
                                        : "Generate License"
                                    }
                                  >
                                    <IconButton
                                      color={
                                        hasActiveLicense
                                          ? "success"
                                          : isExpired || isInactive
                                            ? "error"
                                            : isExpiringSoon
                                              ? "warning"
                                              : "warning"
                                      }
                                      onClick={() =>
                                        handleGenerateLicense(
                                          currentUserDetail.id,
                                          product,
                                        )
                                      }
                                    >
                                      <KeyIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="View License History">
                                    <IconButton
                                      color="info"
                                      onClick={() =>
                                        handleViewLicenseHistory(
                                          currentUserDetail.id,
                                          product,
                                        )
                                      }
                                    >
                                      <HistoryIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )) ||
                            (isLicenseServerAssignment(product) && (
                              <Box>
                                <Tooltip title="Manage zips">
                                  <IconButton
                                    onClick={() =>
                                      setZipTarget({
                                        user: currentUserDetail,
                                        product,
                                      })
                                    }
                                  >
                                    <FolderZipIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))
                          }
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography fontWeight="medium">
                                  {product.name}
                                </Typography>
                                {product.requires_license &&
                                  !isLicenseServerAssignment(product) && (
                                    <Chip
                                      size="small"
                                      label="Requires License"
                                      color="info"
                                      sx={{ height: 20, fontSize: "0.65rem" }}
                                    />
                                  )}
                                {isLicenseServerAssignment(product) && (
                                  <Chip
                                    size="small"
                                    label="License Server"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.65rem" }}
                                  />
                                )}
                                {getLicenseStatusChip(
                                  product,
                                  currentUserDetail.id,
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: 1.4,
                                    maxHeight: "4.2em", // 3 lines * 1.4 line-height
                                    mb: 1,
                                  }}
                                >
                                  {product.description ||
                                    "No description available"}
                                </Typography>

                                {/* License Details */}
                                {product.requires_license &&
                                  !isLicenseServerAssignment(product) &&
                                  license &&
                                  license.license_key && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        p: 1,
                                        bgcolor: "action.hover",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight="bold"
                                      >
                                        License Details:
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        <Typography
                                          variant="caption"
                                          display="block"
                                        >
                                          <strong>Status:</strong>{" "}
                                          {license.status
                                            .charAt(0)
                                            .toUpperCase() +
                                            license.status.slice(1)}
                                        </Typography>
                                        {license.expiry_date && (
                                          <Typography
                                            variant="caption"
                                            display="block"
                                          >
                                            <strong>Expires:</strong>{" "}
                                            {new Date(
                                              license.expiry_date,
                                            ).toLocaleDateString()}
                                          </Typography>
                                        )}
                                        {license.hardware_id && (
                                          <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                              fontFamily: "monospace",
                                              wordBreak: "break-all",
                                            }}
                                          >
                                            <strong>Hardware ID:</strong>{" "}
                                            {license.hardware_id.length > 20
                                              ? `${license.hardware_id.substring(
                                                  0,
                                                  20,
                                                )}...`
                                              : license.hardware_id}
                                          </Typography>
                                        )}
                                        <Typography
                                          variant="caption"
                                          display="block"
                                        >
                                          <strong>Last Updated:</strong>{" "}
                                          {license.updated_at
                                            ? new Date(
                                                license.updated_at,
                                              ).toLocaleDateString()
                                            : "N/A"}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}

                                {/* Show message if no license but requires one */}
                                {product.requires_license &&
                                  !isLicenseServerAssignment(product) &&
                                  (!license ||
                                    license.status === "missing") && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        p: 1,
                                        bgcolor: "rgba(255,193,7,0.1)",
                                        borderRadius: 1,
                                        border: "1px solid rgba(255,193,7,0.3)",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="warning.main"
                                        fontWeight="bold"
                                      >
                                        ⚠️ No license found for this product
                                      </Typography>
                                    </Box>
                                  )}

                                {/* Show warning for expired or expiring soon license */}
                                {product.requires_license &&
                                  !isLicenseServerAssignment(product) &&
                                  license &&
                                  (license.status === "expired" ||
                                    license.status === "expiring_soon") && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        p: 1,
                                        bgcolor:
                                          license.status === "expired"
                                            ? "rgba(244,67,54,0.1)"
                                            : "rgba(255,193,7,0.1)",
                                        borderRadius: 1,
                                        border:
                                          license.status === "expired"
                                            ? "1px solid rgba(244,67,54,0.3)"
                                            : "1px solid rgba(255,193,7,0.3)",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color={
                                          license.status === "expired"
                                            ? "error.main"
                                            : "warning.main"
                                        }
                                        fontWeight="bold"
                                      >
                                        {license.status === "expired"
                                          ? "❌ License has expired"
                                          : "⚠️ License is expiring soon"}
                                      </Typography>
                                    </Box>
                                  )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    No products assigned to this user.
                  </Alert>
                )}

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick(currentUserDetail)}
                  >
                    Manage Product Access
                  </Button>

                  {/* Refresh License Status Button */}
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<LicenseIcon />}
                    onClick={() => loadUserLicenseStatus(currentUserDetail)}
                  >
                    Refresh License Status
                  </Button>
                </Box>
              </TabPanel>

              {/* Hardware and licenses are managed together, per device. */}
              <TabPanel value={currentTabIndex} index={2}>
                <UserProductHardwareIds
                  user={currentUserDetail}
                  onGenerateLicense={handleGenerateLicense}
                  onHardwareUpdated={fetchUsers}
                />
              </TabPanel>

              {/* Download History Tab */}
              <TabPanel value={currentTabIndex} index={3}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Download History
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    View the user's download activity.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleViewDownloadLogs(currentUserDetail.id)}
                  fullWidth
                >
                  View Download Logs
                </Button>
              </TabPanel>
            </Box>
          </>
        )}
      </Drawer>
      {/* Create User Dialog */}
      <Dialog
        open={createUserDialogOpen}
        onClose={() => setCreateUserDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{ backdropFilter: "blur(3px)" }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AddIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Create New User</Typography>
            </Box>
            <IconButton onClick={() => setCreateUserDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            "& .MuiContainer-root": {
              maxWidth: "none !important",
              padding: "0 !important",
            },
          }}
        >
          <RegisterForm
            isAdminCreating={true}
            onUserCreated={handleUserCreated}
          />
        </DialogContent>
      </Dialog>
      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{ backdropFilter: "blur(3px)" }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <EditIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Edit User</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    label="Role"
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Card
              variant="outlined"
              elevation={0}
              sx={{
                border: "none",
              }}
            >
              <CardHeader
                sx={{ paddingBottom: 1 }}
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AssignmentIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1">
                      Assigned Products
                    </Typography>
                  </Box>
                }
                subheader="Select which products this user will have access to"
              />
              <CardContent sx={{ paddingTop: 0 }}>
                <Grid container spacing={1}>
                  {products.map((product) => (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <Card
                        variant="outlined"
                        elevation={1}
                        sx={{
                          height: "100%",
                          border: `2px solid`,
                          borderColor: formData.productIds.includes(product.id)
                            ? "primary.main"
                            : "divider",
                          borderRadius: 2,
                          bgcolor: "background.default",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: formData.productIds.includes(
                              product.id,
                            )
                              ? "primary.main"
                              : "divider",
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.productIds.includes(
                                  product.id,
                                )}
                                onChange={() => handleProductToggle(product.id)}
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body1" fontWeight="medium">
                                  {product.name}
                                </Typography>
                                {product.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {product.description}
                                  </Typography>
                                )}
                                {product.requires_license && (
                                  <Chip
                                    size="small"
                                    label="Requires License"
                                    color="warning"
                                    sx={{ mt: 1, height: 20 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                          {product.requires_license &&
                            formData.productIds.includes(product.id) &&
                            !formData.licenseServerIds.includes(product.id) && (
                              <Box>
                                <TextField
                                  label="Licenses allowed"
                                  helperText="One hardware ID per license. Increase this to allow more devices."
                                  type="number"
                                  size="small"
                                  sx={{ mt: 2 }}
                                  inputProps={{
                                    min: 1,
                                    max: 2147483647,
                                    step: 1,
                                  }}
                                  value={
                                    formData.licenseLimits?.[product.id] ?? 1
                                  }
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      licenseLimits: {
                                        ...prev.licenseLimits,
                                        [product.id]: e.target.value,
                                      },
                                    }))
                                  }
                                />
                                {product.requires_license && (
                                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                                    <TextField
                                      select
                                      label="License type"
                                      size="small"
                                      fullWidth
                                      value={
                                        formData.licenseTerms?.[product.id]
                                          ?.licenseType ?? 1
                                      }
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          licenseTerms: {
                                            ...prev.licenseTerms,
                                            [product.id]: {
                                              ...prev.licenseTerms?.[
                                                product.id
                                              ],
                                              licenseType: Number(
                                                e.target.value,
                                              ),
                                            },
                                          },
                                        }))
                                      }
                                    >
                                      {[
                                        [1, "Trial"],
                                        [2, "NFR"],
                                        [3, "Educational"],
                                        [4, "Subscription"],
                                      ].map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                          {label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <TextField
                                      label="License Validity (months)"
                                      type="number"
                                      size="small"
                                      fullWidth
                                      inputProps={{
                                        min: 1,
                                        max: 2147483647,
                                        step: 1,
                                      }}
                                      helperText="Default and maximum license duration from generation day"
                                      value={
                                        formData.licenseTerms?.[product.id]
                                          ?.licenseMonths ?? 1
                                      }
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          licenseTerms: {
                                            ...prev.licenseTerms,
                                            [product.id]: {
                                              ...prev.licenseTerms?.[
                                                product.id
                                              ],
                                              licenseMonths: e.target.value,
                                            },
                                          },
                                        }))
                                      }
                                    />
                                  </Box>
                                )}
                              </Box>
                            )}
                          {product.requires_license &&
                            formData.productIds.includes(product.id) && (
                              <FormControlLabel
                                sx={{ display: "block", ml: 3 }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={formData.licenseServerIds.includes(
                                      product.id,
                                    )}
                                    onChange={() =>
                                      handleLicenseServerToggle(product.id)
                                    }
                                  />
                                }
                                label={
                                  <Typography variant="caption">
                                    License server user
                                  </Typography>
                                }
                              />
                            )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              elevation={0}
              sx={{
                border: "none",
              }}
            >
              <CardHeader
                sx={{ paddingBottom: 1 }}
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SettingsIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1">
                      Meta Data (JSON)
                    </Typography>
                  </Box>
                }
                subheader="Additional configuration data for this user"
              />
              <CardContent sx={{ paddingTop: 0 }}>
                <TextField
                  fullWidth
                  name="metaString"
                  value={formData.metaString}
                  onChange={handleMetaChange}
                  multiline
                  rows={4}
                  error={
                    formData.metaString &&
                    (() => {
                      try {
                        JSON.parse(formData.metaString);
                        return false;
                      } catch (e) {
                        return true;
                      }
                    })()
                  }
                  helperText={
                    (formData.metaString &&
                      (() => {
                        try {
                          JSON.parse(formData.metaString);
                          return "Valid JSON format";
                        } catch (e) {
                          return "Invalid JSON format: " + e.message;
                        }
                      })()) ||
                    "Enter valid JSON data"
                  }
                  InputProps={{
                    sx: {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            startIcon={<LicenseIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      {/* Download Logs Dialog */}
      <UserDownloadLogs
        open={downloadLogsOpen}
        onClose={() => setDownloadLogsOpen(false)}
        userId={selectedUserId}
      />
      {/* License Generation Dialog */}
      <Dialog
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KeyIcon color="secondary" />
            <Typography variant="h6">Generate License</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="subtitle1"
            color="primary"
            gutterBottom
            fontWeight="medium"
          >
            {selectedProduct?.name}
          </Typography>

          <Box component="form" onSubmit={handleGenerateLicense} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <LicenseTypeSelector
                  value={licenseFormData.licenseType}
                  onChange={handleLicenseTypeChange}
                  required
                  helperText="Select the type of license to generate"
                  size="medium"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Hardware ID"
                  name="hardwareId"
                  value={licenseFormData.hardwareId}
                  onChange={handleLicenseFormChange}
                  helperText="Must be a valid base64 string provided by the user"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={licenseFormData.companyName}
                  onChange={handleLicenseFormChange}
                  helperText="Optional: User's company name to include in the license"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  name="expireDate"
                  type="date"
                  value={licenseFormData.expireDate}
                  onChange={handleLicenseFormChange}
                  helperText="Defaults to the maximum date allowed by the assigned license Validity"
                  inputProps={(() => {
                    const range = licenseDateRange(
                      selectedProduct?.UserProduct?.license_months ??
                        selectedProduct?.user_products?.license_months ??
                        1,
                    );
                    return { min: range.min, max: range.max };
                  })()}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setLicenseDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateLicense}
            variant="contained"
            color="primary"
            startIcon={<KeyIcon />}
          >
            Generate License
          </Button>
        </DialogActions>
      </Dialog>
      {/* License History Dialog */}
      <Dialog
        open={licenseHistoryDialogOpen}
        onClose={() => setLicenseHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HistoryIcon color="info" />
            <Typography variant="h6">License History</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="subtitle1"
            color="primary"
            gutterBottom
            fontWeight="medium"
          >
            {selectedProduct?.name}
          </Typography>

          {licenseHistory.length > 0 ? (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                mt: 2,
                boxShadow: "none",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "divider",
                },
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: "bold" }}>
                      License Key
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Hardware ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Expiry Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Created At
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Created By
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Approved By
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {licenseHistory.map((license, index) => (
                    <TableRow
                      key={license.id}
                      hover
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        "& td": {
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: 150,
                              fontFamily: "monospace",
                              bgcolor: "action.hover",
                              p: 1,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                            }}
                          >
                            {license.license_key}
                          </Typography>
                          <Tooltip title="Copy License Key">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  license.license_key,
                                );
                                alert("License key copied to clipboard!");
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {license.hardware_id && (
                            <Tooltip title={license.hardware_id}>
                              <Typography
                                noWrap
                                sx={{
                                  maxWidth: 100,
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <span>{license.hardware_id}</span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      license.hardware_id,
                                    );
                                    alert("Hardware ID copied to clipboard!");
                                  }}
                                  sx={{ ml: 1, p: 0.3 }}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Typography>
                            </Tooltip>
                          )}
                          {license.hardware_ids &&
                            license.hardware_ids.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  +{license.hardware_ids.length - 1} more
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(license.expiry_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(license.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={license.is_active ? "Active" : "Inactive"}
                          color={license.is_active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {license.creator ? (
                          <AdminInfoDisplay
                            admin={license.creator}
                            label=""
                            showRole={false}
                            size="small"
                          />
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {license.approvedBy ? (
                          <AdminInfoDisplay
                            admin={license.approvedBy}
                            label=""
                            showRole={false}
                            size="small"
                          />
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No license history found for this user-product combination.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setLicenseHistoryDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <KeyIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Change Password for {selectedUser?.email}
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  name="newPassword"
                  value={passwordFormData.newPassword}
                  onChange={handlePasswordFormChange}
                  error={!!passwordErrors.newPassword}
                  helperText={
                    passwordErrors.newPassword ||
                    "Minimum 8 characters required"
                  }
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  name="confirmPassword"
                  value={passwordFormData.confirmPassword}
                  onChange={handlePasswordFormChange}
                  error={!!passwordErrors.confirmPassword}
                  helperText={
                    passwordErrors.confirmPassword ||
                    "Re-enter the new password"
                  }
                />
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This will immediately change the
                user's password. The user will need to use the new password for
                their next login.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setPasswordDialogOpen(false)}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<KeyIcon />}
            >
              Change Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <UserVersionFilesManager
        open={Boolean(zipTarget)}
        onClose={() => setZipTarget(null)}
        mode="user"
        userId={zipTarget?.user?.id}
        userEmail={zipTarget?.user?.email}
        productId={zipTarget?.product?.id}
        productName={zipTarget?.product?.name}
      />
    </Container>
  );
};

export default UserManagementPage;
